
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { resetSessionState } from "@/services/auth/sessionUtils";
import { clearReagentsCache } from "@/services/reagents/fetchReagents";
import { toast } from "sonner";

/**
 * Hook for handling manual reagent data refresh
 */
export const useReagentRefresh = (refetchReagents: () => Promise<any>) => {
  const queryClient = useQueryClient();
  const [isRetrying, setIsRetrying] = useState(false);

  const handleManualRefresh = async () => {
    setIsRetrying(true);
    toast.info("Refreshing data...");
    
    try {
      // Reset session state to ensure we get a fresh token
      resetSessionState();
      
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) {
        toast.error("Authentication error: " + error.message);
        setIsRetrying(false);
        return;
      }
      
      if (!session) {
        toast.error("No active session. Please log in again.");
        setIsRetrying(false);
        return;
      }
      
      // Force refresh session
      const { error: refreshError } = await supabase.auth.refreshSession();
      if (refreshError) {
        toast.error("Session refresh failed: " + refreshError.message);
        setIsRetrying(false);
        return;
      }
      
      // Clear the cache to ensure fresh data
      clearReagentsCache();
      
      // Invalidate and refetch
      await queryClient.invalidateQueries({ queryKey: ['reagents'] });
      await queryClient.invalidateQueries({ queryKey: ['categories'] });
      await refetchReagents();
      
      toast.success("Data refreshed successfully");
    } catch (error: any) {
      console.error("Refresh error:", error);
      toast.error("Failed to refresh data: " + (error.message || "Unknown error"));
    } finally {
      setIsRetrying(false);
    }
  };

  return {
    isRetrying,
    handleManualRefresh
  };
};
